import { ISODateString } from "next-auth"

export function convertSecondsToDurationString(seconds: number | null) {
  if (!seconds) {
    return "effort unknown"
  }
  if (seconds < 60) {
    return "just a few seconds"
  }

  if (seconds < 300) {
    return "a couple of minutes"
  }

  if (seconds < 1500) {
    return "a few minutes"
  }

  if (seconds < 2000) {
    return "half an hour"
  }

  if (seconds < 4000) {
    return "an hour"
  } else {
    return "a few hours"
  }
}

export function convertDateTimeToTimePassedString(
  dateTime: Date | string
): string {
  const dateTimeDate = dateTime instanceof Date ? dateTime : new Date(dateTime)
  const now = new Date()
  const diff = now.getTime() - dateTimeDate.getTime()
  const diffSeconds = Math.floor(diff / 1000)
  const diffMinutes = Math.floor(diff / 1000 / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`
  }
  if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`
  }
  if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`
  }
  if (diffSeconds >= 15) {
    return `a few seconds ago`
  }
  return `just now`
}

export function formatTime(epochTime: number): string {
  const date = new Date(epochTime)

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`

  return date.toUTCString() // Example Output: "Tue, 03 Dec 2024 10:00:00 GMT"
}

export function formatDeadline(deadline: ISODateString) {
  const date = new Date(deadline)
  const timeDiffMillis = date.getTime() - Date.now()
  const isInPast = timeDiffMillis < 0
  const timeDiffMillisAbs = Math.abs(timeDiffMillis)
  const timeDiffDays = Math.floor(timeDiffMillisAbs / 1000 / 60 / 60 / 24)

  // Return "In {minutes} minutes" if deadline is in the next 15 minutes
  if (timeDiffMillisAbs < 15 * 60 * 1000) {
    return `${isInPast ? "Overdue" : "In"} ${Math.floor(
      timeDiffMillisAbs / 1000 / 60
    )} minutes`
  }

  // Return "In 15 minutes" if deadline is in the next 30 minutes
  if (timeDiffMillisAbs < 30 * 60 * 1000) {
    return `${isInPast ? "Overdue" : "In"} 15 mins`
  }

  // Return "In 30 mins" if deadline is in the next 45 minutes
  if (timeDiffMillisAbs < 45 * 60 * 1000) {
    return `${isInPast ? "Overdue" : "In"} half an hour`
  }

  // Return "In 1 hour" if deadline is in the next 60 minutes
  if (timeDiffMillisAbs < 60 * 60 * 1000) {
    return `${isInPast ? "Overdue" : "In"} 1 hour`
  }

  // Return "In {hours} hours" if deadline is in the next 10 hours
  if (timeDiffMillisAbs < 10 * 60 * 60 * 1000) {
    return `${isInPast ? "Overdue" : "In"} ${Math.floor(
      timeDiffMillisAbs / 1000 / 60 / 60
    )} hours`
  }

  // Return "Yesterday" if deadline is end of the day, like 2024-12-03T23:59:00.000Z
  if (date.getDate() === new Date().getDate() - 1) {
    return "Yesterday"
  }

  // Return "Today" if deadline is end of the day, like 2024-12-04T23:59:00.000Z
  if (date.getDate() === new Date().getDate()) {
    return "Today"
  }

  // Return "Tomorrow" if deadline is end of the day, like 2024-12-05T23:59:00.000Z
  if (date.getDate() === new Date().getDate() + 1) {
    return "Tomorrow"
  }

  // Return "In {days} days" if deadline is in the next 10 days
  if (timeDiffDays <= 13) {
    return `${isInPast ? "Overdue" : "In"} ${timeDiffDays} days`
  }

  // Return "In {weeks} weeks" if deadline is in the next 10 weeks
  if (timeDiffDays > 13) {
    return `${isInPast ? "Overdue" : "In"} ${Math.floor(
      timeDiffMillisAbs / 1000 / 60 / 60 / 24 / 7
    )} weeks`
  }

  return "No deadline"
}

/**
 * Converts a Unix timestamp (in seconds) to a UTC Date object
 * @param unixTimestamp - Unix timestamp in milliseconds (e.g., 1731135687000)
 * @returns Date object in UTC
 */
export function unixTimeToUTC(epochTime: number): Date {
  // Unix timestamps are in seconds, JavaScript Date expects milliseconds
  return new Date(epochTime)
}

/**
 * Converts a Unix timestamp (in seconds) to a UTC string
 * @param epochTime - Unix timestamp in milliseconds (e.g., 1731135687000)
 * @returns UTC string (e.g., "Tue, 09 Nov 2024 10:00:00 GMT")
 */
export function unixTimeToUTCStringFormatted(epochTime: number): string {
  return unixTimeToUTC(epochTime).toUTCString()
}
